/**
 * Web boot — hands the shared package this app's environment.
 *
 * Nothing under shared/ reads import.meta.env, because the same code is
 * bundled by Metro for the mobile app and Metro has no such thing. Reading it
 * is this file's job; zivapp-mobile/boot.js is the mirror of it.
 *
 * Imported first in main.jsx so the configuration is in place before anything
 * calls a service.
 */

import { configureZivapp, auth, chats, contacts, messages, status, channels, communities } from '@zivapp/shared'

const env = import.meta.env

configureZivapp({
  backend:  env.VITE_BACKEND ?? 'rest',
  dev:      env.DEV,
  platform: 'web',

  firebase: {
    apiKey:            env.VITE_FIREBASE_API_KEY,
    authDomain:        env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId:         env.VITE_FIREBASE_PROJECT_ID,
    storageBucket:     env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId:             env.VITE_FIREBASE_APP_ID
  },

  apiUrl: env.VITE_API_URL ?? 'http://localhost:4000',
  wsUrl:  env.VITE_WS_URL ?? 'ws://localhost:4000',

  // Synchronous on web; the mobile app passes an AsyncStorage wrapper with the
  // same three methods, which is why the shared code awaits all of them.
  tokenStorage: {
    get:    key => localStorage.getItem(key),
    set:    (key, value) => localStorage.setItem(key, value),
    remove: key => localStorage.removeItem(key)
  },

  /**
   * Firebase will not send an SMS without proof the request came from a real
   * browser. On the web that proof is an invisible reCAPTCHA, which needs a
   * DOM node to mount in — so it is built here rather than in the shared
   * package, which Metro also bundles and where `document` does not exist.
   *
   * The class and the auth instance arrive as arguments so this file never
   * has to import from 'firebase/*' itself; that stays the provider's job.
   */
  phoneVerifier: ({ RecaptchaVerifier, firebaseAuth }) => {
    let host = document.getElementById('ziv-recaptcha')
    if (!host) {
      host = document.createElement('div')
      host.id = 'ziv-recaptcha'
      host.style.display = 'none'
      document.body.appendChild(host)
    }
    return new RecaptchaVerifier(firebaseAuth, host, { size: 'invisible' })
  }
})

/**
 * Expose the service layer on window, for tests only.
 *
 * The real-time browser test needs to find a user by phone and open a chat
 * before the contacts screen exists. Behind an explicit build flag rather
 * than import.meta.env.DEV, because the build it runs against is a
 * production one — and because a normal production bundle must never hand
 * the page a way to call the API as the signed-in user.
 *
 *   VITE_EXPOSE_SERVICES=1 npm run build:web
 */
if (env.VITE_EXPOSE_SERVICES === '1') {
  window.__ziv = { auth, chats, contacts, messages, status, channels, communities }
  console.warn('[ZIvapp] services are exposed on window.__ziv — test build only.')
}
